import { inject, Injectable } from '@angular/core';
import { DateTime } from 'luxon';

import { GuestReservations } from 'src/providers/guest-reservations/guest-reservations';
import { Schedule } from 'src/providers/schedule/schedule';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { WindowService } from 'src/shared/providers/window.service';

import { JobPage } from 'src/pages/schedule/job/job';
import { PastJobPage } from 'src/pages/more/past-job/past-job';
import { PastPrivateJobPage } from 'src/pages/more/past-private-job/past-private-job';
import { ReservationPage } from 'src/pages/reservation/reservation';

import { FinancialTransaction } from './property-transactions.service';
import { SelectModel } from 'src/tidy-ui-components/components/form/select/select.model';

export interface RelatedJobSummary {
  id: number;
  /** The service the schedule shows, e.g. "1 hour cleaning". */
  service: string;
  dateLabel: string;
  /** The service category icon the backend sends for the schedule, or the schedule's fallback. */
  iconUrl: string;
  label: string;
}

export interface RelatedReservationSummary {
  id: number;
  /** "Sep 15 – Sep 23, 2026" */
  dateLabel: string;
  source: string;
  /** The booker, else the first staying guest; empty when the stay names nobody. */
  guestName: string;
  label: string;
}

export interface RelatedRecords {
  jobs: Map<number, RelatedJobSummary>;
  reservations: Map<number, RelatedReservationSummary>;
}

/** Same fallback the schedule uses when a service has no icon. */
export const JOB_ICON_FALLBACK = 'assets/img/mop.png';
export const RESERVATION_ICON = 'assets/svg/bed-outline.svg';
/** A coloured stand-in for the guest avatar when the stay names no guest. */
export const RESERVATION_COLOR_ICON = 'assets/img/reservation-green.svg';

const PAST_PROGRESS = ['completed', 'client_cancelled', 'homekeeper_cancelled', 'cancelled'];
const FINANCE_TRANSACTIONS_ROUTE = 'finance?tab=transactions';
const JOB_WINDOW_DAYS = 90;
const RESERVATIONS_PER_PAGE = 200;
const DATE_FORMATS = ['MMM d, yyyy', 'MM/dd/yyyy', 'yyyy-MM-dd'];

/**
 * The job that wrote a TIDY row and the reservation a row's money belongs to.
 * Resolves the ids the API sends into summaries (service, dates, icon), lists a
 * property's reservations to pick from, and opens a record the way the schedule
 * does: scheduled, past and past private jobs are three pages, and the
 * reservation page needs the stay's payload rather than its id.
 */
@Injectable({ providedIn: 'root' })
export class TransactionRelatedRecordsService {
  private readonly schedule = inject(Schedule);
  private readonly guestReservations = inject(GuestReservations);
  private readonly navCtrl = inject(CustomNavController);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly windowService = inject(WindowService);

  private readonly reservationsByAddress = new Map<number, Promise<any[]>>();
  private readonly jobsByWindow = new Map<string, Promise<any[]>>();

  jobIdOf(txn: FinancialTransaction): number | null {
    return txn.source_record_type === 'Job' && txn.source_record_id ? txn.source_record_id : null;
  }

  /** The stay the money belongs to, stored on its link; a stay's own row answers itself. */
  reservationIdOf(txn: FinancialTransaction): number | null {
    if (txn.guest_reservation_id) { return txn.guest_reservation_id; }
    return txn.source_record_type === 'GuestReservation' && txn.source_record_id ? txn.source_record_id : null;
  }

  /**
   * Summaries for every job and reservation the rows point at: one request per
   * property for reservations, one per property and date window for jobs. Best
   * effort; a miss leaves the id.
   */
  async recordsFor(rows: FinancialTransaction[]): Promise<RelatedRecords> {
    const records: RelatedRecords = { jobs: new Map(), reservations: new Map() };
    const reservationAddresses = new Set<number>();
    const jobRowsByAddress = new Map<number, FinancialTransaction[]>();
    for (const row of rows) {
      if (!row.address_id) { continue; }
      if (this.reservationIdOf(row)) { reservationAddresses.add(row.address_id); }
      if (this.jobIdOf(row)) {
        jobRowsByAddress.set(row.address_id, [...(jobRowsByAddress.get(row.address_id) ?? []), row]);
      }
    }
    await Promise.all([
      ...[...reservationAddresses].map(async addressId => {
        for (const reservation of await this.reservationsOf(addressId)) {
          records.reservations.set(reservation.id, this.toReservationSummary(reservation));
        }
      }),
      ...[...jobRowsByAddress].map(async ([addressId, addressRows]) => {
        const dates = addressRows.map(row => row.period_start_date).sort();
        for (const job of await this.jobsOf(addressId, dates[0], dates[dates.length - 1])) {
          records.jobs.set(job.id, this.toJobSummary(job));
        }
      }),
    ]);
    return records;
  }

  async jobSummary(addressId: number, jobId: number, aroundDate: string): Promise<RelatedJobSummary | null> {
    const job = (await this.jobsOf(addressId, aroundDate, aroundDate)).find(candidate => candidate.id === jobId);
    return job ? this.toJobSummary(job) : null;
  }

  async reservationSummary(addressId: number, reservationId: number): Promise<RelatedReservationSummary | null> {
    const reservation = (await this.reservationsOf(addressId)).find(candidate => candidate.id === reservationId);
    return reservation ? this.toReservationSummary(reservation) : null;
  }

  /** Reservations of the property, newest first, as select items. */
  async reservationOptions(addressId: number): Promise<SelectModel[]> {
    const reservations = await this.reservationsOf(addressId);
    return reservations
      .slice()
      .sort((a, b) => this.parseDate(b.check_in_date).toMillis() - this.parseDate(a.check_in_date).toMillis())
      .map(reservation => ({ value: String(reservation.id), viewValue: this.toReservationSummary(reservation).label }));
  }

  /** Same decision as the schedule: past + private, past, or scheduled. The card is fetched for its state. */
  async openJob(jobId: number, addressId: number | null): Promise<void> {
    const card = await this.schedule.getJobDetail(jobId);
    const job = card?.job ?? card ?? {};
    const resolvedAddressId = addressId ?? job.address_id ?? card?.address?.id ?? null;
    const isPrivate = !!(job.is_private ?? card?.is_private);
    const isPast = this.isPastJob(job, card);
    const params = {
      jobId,
      addressId: resolvedAddressId,
      bookingId: card?.booking?.id ?? job.booking_id ?? null,
      backPage: 'finance',
    };
    let route = `job/${resolvedAddressId}/${jobId}`;
    let component: any = JobPage;
    if (isPast && isPrivate) {
      route = `past-private-job/${jobId}`;
      component = PastPrivateJobPage;
    } else if (isPast) {
      route = `past-job/${jobId}`;
      component = PastJobPage;
    }
    if (this.windowService.isDesktopRes) {
      await this.rightSidePanelService.navigateTo(route, params, component);
      return;
    }
    // The job pages navigate to this value on Back; without the tab, Finance opens on Bills.
    localStorage.setItem('pastJobBackPage', FINANCE_TRANSACTIONS_ROUTE);
    localStorage.setItem('jobBackPage', FINANCE_TRANSACTIONS_ROUTE);
    this.navCtrl.navigateForward(route, params);
  }

  /**
   * The reservation page reads id, dates and guests off the object it is handed, the way
   * the Reservations list hands it a list row. The details endpoint answers without an
   * id, so the list row is used when the property's list holds it, and the details are
   * merged under the id only as a fallback.
   */
  async openReservation(reservationId: number, addressId: number | null, addressLabel: string): Promise<void> {
    const listed = addressId ? (await this.reservationsOf(addressId)).find(candidate => candidate.id === reservationId) : null;
    const guestReservation: any = listed
      ?? { id: reservationId, ...(await this.guestReservations.getReservationById(reservationId) as any) };
    const params = {
      guestReservation,
      isInquiry: false,
      address: addressLabel || guestReservation?.address?.address_name || guestReservation?.address?.address1 || 'Property',
      addressId: addressId ?? guestReservation?.address_id ?? guestReservation?.address?.id ?? null,
    };
    if (this.windowService.isDesktopRes) {
      this.rightSidePanelService.setDialogPageTitle('Reservation');
      await this.rightSidePanelService.navigateTo('reservation', params, ReservationPage);
      return;
    }
    this.navCtrl.navigateForward('reservation', params);
  }

  private reservationsOf(addressId: number): Promise<any[]> {
    if (!this.reservationsByAddress.has(addressId)) {
      // `includes=guests` rides the same request, so the guest avatar costs nothing extra.
      const request = this.guestReservations
        .getReservations({ address_id: addressId, per_page: RESERVATIONS_PER_PAGE, includes: 'guests' } as any)
        .then(response => response?.body ?? [])
        .catch(() => []);
      this.reservationsByAddress.set(addressId, request);
    }
    return this.reservationsByAddress.get(addressId);
  }

  /** Real jobs (not requests) of the property between the two dates, padded by the window. */
  private jobsOf(addressId: number, fromDate: string, toDate: string): Promise<any[]> {
    const start = this.parseDate(fromDate).minus({ days: JOB_WINDOW_DAYS }).toISODate();
    const end = this.parseDate(toDate).plus({ days: JOB_WINDOW_DAYS }).toISODate();
    const key = `${addressId}:${start}:${end}`;
    if (!this.jobsByWindow.has(key)) {
      const request = this.schedule
        .getScheduleCalendar(start, end, { address_ids: [addressId] }, 'jobs')
        .then((days: any[]) => (Array.isArray(days) ? days : []).flatMap(day => day?.jobs ?? []).filter(job => job?.id && !job.is_job_request))
        .catch(() => []);
      this.jobsByWindow.set(key, request);
    }
    return this.jobsByWindow.get(key);
  }

  private toJobSummary(job: any): RelatedJobSummary {
    const service = job?.service_type_details?.name || job?.service_name || 'Job';
    const date = this.parseDate(job?.date);
    const dateLabel = date.isValid ? date.toFormat('MMM d, yyyy') : '';
    return {
      id: job?.id,
      service,
      dateLabel,
      iconUrl: job?.service_icon_url || JOB_ICON_FALLBACK,
      label: dateLabel ? `${service} · ${dateLabel}` : `${service} #${job?.id}`,
    };
  }

  private toReservationSummary(reservation: any): RelatedReservationSummary {
    const checkIn = this.parseDate(reservation?.check_in_date);
    const checkOut = this.parseDate(reservation?.check_out_date);
    const dateLabel = checkIn.isValid && checkOut.isValid
      ? `${checkIn.toFormat('MMM d')} – ${checkOut.toFormat('MMM d, yyyy')}`
      : `Reservation #${reservation?.id}`;
    const rawSource = reservation?.guest_reservation_source_type?.name;
    // A stay the customer entered reads "Added manually", as the reservation page says it.
    const source = rawSource === 'Add Manually' ? 'Added manually' : rawSource || '';
    const guests: any[] = reservation?.guests ?? [];
    const guest = guests.find(g => g?.is_booker) ?? guests.find(g => g?.is_staying) ?? guests[0];
    return {
      id: reservation?.id,
      dateLabel,
      source,
      guestName: guest?.name || '',
      label: source ? `${dateLabel} · ${source}` : dateLabel,
    };
  }

  /** The card's own state when it says so; otherwise the job's date against today. */
  private isPastJob(job: any, card: any): boolean {
    if (typeof job?.state === 'string') { return job.state !== 'scheduled'; }
    const progress = job?.job_progress ?? card?.job_progress;
    if (typeof progress === 'string') { return PAST_PROGRESS.includes(progress); }
    const status = String(job?.status ?? '').toLowerCase();
    if (/cancel|complet/.test(status)) { return true; }
    if (/scheduled|upcoming|pending/.test(status)) { return false; }
    const date = this.parseDate(job?.date);
    return date.isValid && date < DateTime.local().startOf('day');
  }

  private parseDate(value: string | null | undefined): DateTime {
    if (!value) { return DateTime.invalid('empty'); }
    const iso = DateTime.fromISO(value);
    if (iso.isValid) { return iso; }
    for (const format of DATE_FORMATS) {
      const parsed = DateTime.fromFormat(value, format);
      if (parsed.isValid) { return parsed; }
    }
    return DateTime.invalid('unparsed');
  }
}
